import api from '../../api'
import axios from 'axios'
import { UsuarioModel } from '../../models/UsuarioModel'
import { ProgressoModel } from '../../models/ProgressoModel'
import { MatriculaModel } from '../../models/MatriculaModel'
import { ValidacaoFacialModel } from '../../models/ValidacaoFacialModel'
import { toast } from 'react-toastify'
import { EstatisticaEstudoModel } from '../../models/EstatisticaEstudoModel'
import { createSimpleStore } from 'react-simple-reducer'

const normalizeCapitalizeName = (name: string) =>
  name
    .toLowerCase()
    .split(/ /g)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .map((s) => (['De', 'Da', 'Do', 'Dos', 'Das'].includes(s) ? s.toLowerCase() : s))
    .join(' ')

const normalizeMunicipio = (municipio: any) => {
  if (typeof municipio === 'string') {
    return { nome: normalizeCapitalizeName(municipio) }
  }

  const nome = municipio?.nome ?? municipio?.name
  if (!nome) return null

  return { ...municipio, nome: normalizeCapitalizeName(nome) }
}

const sortMunicipiosByNome = (a: any, b: any) => a.nome.localeCompare(b.nome, 'pt-BR')

const getPublicJson = async (url: string, params?: Record<string, string>) => {
  const response = await axios.get(url, {
    params,
    withCredentials: false,
    headers: {
      platform: undefined,
    },
  })

  return response.data
}

export const ProgressoStore = createSimpleStore(
  {
    matricula: null as MatriculaModel,
    dadosAluno: null as UsuarioModel | null,
    sessoesEstudo: [] as any[],
    progressos: [] as ProgressoModel[],
    validacoesFaciais: [] as ValidacaoFacialModel[],
    estatisticasDeEstudo: [] as EstatisticaEstudoModel[],
    avaliacoes: [] as any[],
    outrosCursos: [] as any,
    token: null as string | null,
    estados: [] as any[],
    municipios: [] as any[],
    loading: false,
  },
  {
    getDadosSuccess(
      state,
      {
        matricula,
        dadosAluno,
        sessoesEstudo,
        estatisticasEstudo,
        validacoesFaciais,
        avaliacoes,
        progressos,
        outrosCursos,
      }
    ) {
      state.matricula = matricula
      state.dadosAluno = dadosAluno
      state.sessoesEstudo = sessoesEstudo
      state.validacoesFaciais = validacoesFaciais
      state.avaliacoes = avaliacoes
      state.estatisticasDeEstudo = estatisticasEstudo
      state.progressos = progressos
      state.outrosCursos = outrosCursos
    },
    getDadosAlunoSuccess(state, dadosAluno) {
      state.dadosAluno = dadosAluno
    },
    updateDadosAluno(state, dadosAluno) {
      state.dadosAluno = { ...state.dadosAluno, ...dadosAluno }
    },
    gerarNovoTokenSuccess(state, token) {
      state.token = token
    },
    reemitirBoletoSuccess(state, newCobranca) {
      state.matricula.cobranca = newCobranca
      state.matricula.pagamento.referenciaPagamento = newCobranca.id
    },
    getDadosEstados(state, estados) {
      state.estados = estados
    },
    getDadosMunicipio(state, municipio) {
      state.municipios = municipio
    },
    setLoading(state, payload) {
      state.loading = payload
    },
    changePermiteEmissaoCertificado(state, payload: boolean) {
      state.matricula.permiteEmissaoCertificado = payload
    },
  },
  {
    thunks: {
      getDadosAluno() {
        return async (dispatch, getState) => {
          try {
            const dadosAluno = await api.Usuarios.getByCpf(getState().dadosAluno.cpf)
            dispatch(ProgressoStore.actions.getDadosAlunoSuccess(dadosAluno))
          } catch (error) {
            const message = error?.response?.data.message ?? 'Erro ao buscar dados do aluno!'
            toast(message)
          }
        }
      },
      getDados(alunoId: number, matriculaId: number) {
        return async (dispatch) => {
          try {
            dispatch(ProgressoStore.actions.setLoading(true))
            const dados = await getDados(alunoId, matriculaId)

            dispatch(
              ProgressoStore.actions.getDadosSuccess({
                matricula: dados.matricula,
                outrosCursos: dados.outrosCursos,
                ...dados.progressos,
              })
            )
          } catch (error) {
            const message = error?.response?.data.message ?? 'Erro ao buscar dados!'
            toast(message)
          } finally {
            dispatch(ProgressoStore.actions.setLoading(false))
          }
        }
      },
      updateDadosAluno() {
        return async (dispatch, getState) => {
          try {
            const {
              cpf,
              nome,
              telefone,
              email,
              estado,
              municipio,
              dataPrimeiraHabilitacao,
              ignorarValidacaoSerpro,
              dataNascimento,
              rg,
              categoriaCnh,
              cnh,
              renach,
            } = getState().dadosAluno

            const matriculaId = getState().matricula.id

            await api.Usuarios.update(cpf, {
              nome,
              telefone,
              email,
              estado,
              municipio,
              dataPrimeiraHabilitacao,
              ignorarValidacaoSerpro,
              dataNascimento,
              rg,
              categoriaCnh,
              matriculaId,
              cnh,
              renach,
            })

            toast('Dados do aluno atualizados com successo!')
          } catch (error) {
            const message = error?.response?.data.message ?? 'Erro ao atualizar dados do aluno!'
            toast(message)
          }
        }
      },
      gerarNovoToken() {
        return async (dispatch, getState) => {
          try {
            const dadosAluno = getState().dadosAluno
            const data = await api.Usuarios.createTokenMudancaSenha({ usuarioId: dadosAluno.id })
            dispatch(ProgressoStore.actions.gerarNovoTokenSuccess(data))
          } catch (error) {
            const message = error?.response?.data.message ?? 'Erro ao gerar novo token!'
            toast(message)
          }
        }
      },
      reemitirCobranca() {
        return async (dispatch, getState) => {
          try {
            const newCobranca = await api.Matriculas.reemitirCobranca(getState().matricula?.id)
            dispatch(ProgressoStore.actions.reemitirBoletoSuccess(newCobranca))
            toast('Cobrança reemitida com sucesso!')
          } catch (error) {
            const message = error?.response?.data.message ?? 'Erro ao reemitir Cobrança!'
            toast(message)
          }
        }
      },
      getEstados() {
        return async (dispatch) => {
          try {
            const estados = await getPublicJson(
              'https://servicodados.ibge.gov.br/api/v1/localidades/estados',
              { orderBy: 'nome' }
            )
            dispatch(ProgressoStore.actions.getDadosEstados(estados))
          } catch (error: any) {
            const message = error?.response?.data?.message ?? 'Erro ao buscar os municípios!'
            toast(message)
          }
        }
      },
      getMunicipios({ estados }) {
        return async (dispatch) => {
          if (!estados) {
            dispatch(ProgressoStore.actions.getDadosMunicipio([]))
            return
          }

          try {
            let municipios: any[] = []

            try {
              const brasilApiData = await getPublicJson(
                `https://brasilapi.com.br/api/ibge/municipios/v1/${estados}?providers=dados-abertos-br,gov,wikipedia`
              )

              municipios = (Array.isArray(brasilApiData) ? brasilApiData : [])
                .map(normalizeMunicipio)
                .filter(Boolean)
            } catch {
              const ibgeData = await getPublicJson(
                `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${estados}/municipios`
              )

              municipios = (Array.isArray(ibgeData) ? ibgeData : [])
                .map(normalizeMunicipio)
                .filter(Boolean)
            }

            dispatch(
              ProgressoStore.actions.getDadosMunicipio(municipios.sort(sortMunicipiosByNome))
            )
          } catch (error: any) {
            const message = error?.response?.data?.message ?? 'Erro ao buscar os municípios!'
            toast(message)
          }
        }
      },
      cancelarMatricula(matricula: MatriculaModel) {
        return async (dispatch) => {
          try {
            const cancelarMatricula = await axios
              .put('/matriculas/cancelar-matricula', { matriculaId: matricula.id })
              .then((res) => res.data)

            toast.success(cancelarMatricula?.successMessage ?? 'Matrícula cancelada com sucesso!')

            const dados = await getDados(matricula.alunoId, matricula.id)

            dispatch(
              ProgressoStore.actions.getDadosSuccess({
                matricula: dados.matricula,
                outrosCursos: dados.outrosCursos,
                ...dados.progressos,
              })
            )
          } catch (error) {
            if (error?.response?.data) {
              toast.error(error?.response?.data?.message)
            }
          }
        }
      },
      cancelarConclusaoMatricula(matricula: MatriculaModel) {
        return async (dispatch) => {
          try {
            const cancelarConclusao = await axios
              .put('/matriculas/cancelar-conclusao', { matriculaId: matricula.id })
              .then((res) => res.data)

            toast.success(
              cancelarConclusao?.successMessage ?? 'Conclusão do curso cancelado com sucesso!'
            )

            const dados = await getDados(matricula.alunoId, matricula.id)

            dispatch(
              ProgressoStore.actions.getDadosSuccess({
                matricula: dados.matricula,
                outrosCursos: dados.outrosCursos,
                ...dados.progressos,
              })
            )
          } catch (error) {
            if (error?.response?.data) {
              toast.error(error?.response?.data?.message)
            }
          }
        }
      },
      consultarMatricula(matricula: MatriculaModel) {
        return async (dispatch) => {
          try {
            const validar = await axios
              .post('/matriculas/validar', {
                cpf: matricula.aluno.cpf,
                dataNascimento: matricula.aluno.dataNascimento,
                cnh: matricula.aluno.cnh,
                cursoId: matricula.cursoId,
                renach: matricula.renach,
                uf: matricula.uf,
                dataPrimeiraHabilitacao: matricula.aluno.dataPrimeiraHabilitacao,
                municipio: matricula.aluno.municipio,
                categoriaCnh: matricula.aluno.categoriaCnh,
              })
              .then((res) => res.data)

            if (validar?.success) {
              toast.success(validar?.successMessage ?? 'Validação feita com sucesso!')
            } else {
              toast.error(validar?.errorMessage ?? 'Erro ao validar matrícula!')
            }

            const dados = await getDados(matricula.alunoId, matricula.id)

            dispatch(
              ProgressoStore.actions.getDadosSuccess({
                matricula: dados.matricula,
                outrosCursos: dados.outrosCursos,
                ...dados.progressos,
              })
            )
          } catch (error) {
            if (error?.response?.data?.message) {
              toast.error(error?.response?.data?.message)
            }
          }
        }
      },
      confirmarPagamentoManual({
        matriculaId,
        meioPagamento,
        provedorPagamento,
        descricaoConfirmacao,
      }: {
        matriculaId: number
        meioPagamento: 'PIX' | 'BOLETO' | 'CARTAO' | 'ISENTO'
        provedorPagamento?: 'MERCADO_PAGO' | 'EFI' | 'HYPERPAY' | null
        descricaoConfirmacao: string
      }) {
        return async (dispatch, getState) => {
          try {
            dispatch(ProgressoStore.actions.setLoading(true))
            await api.Pagamentos.confirmarManual({
              matriculaId,
              meioPagamento,
              provedorPagamento,
              descricaoConfirmacao,
            })
            const { dadosAluno } = getState()
            const dados = await getDados(dadosAluno.id, matriculaId)
            dispatch(
              ProgressoStore.actions.getDadosSuccess({
                matricula: dados.matricula,
                outrosCursos: dados.outrosCursos,
                ...dados.progressos,
              })
            )
            toast.success('Pagamento confirmado com sucesso!')
          } catch (error) {
            const message = error?.response?.data?.message ?? 'Erro ao confirmar pagamento!'
            toast.error(message)
          } finally {
            dispatch(ProgressoStore.actions.setLoading(false))
          }
        }
      },
      updatePermissaoCertificado(matriculaId: number, permiteEmissaoCertificado: boolean) {
        return async (dispatch, getState) => {
          try {
            dispatch(ProgressoStore.actions.setLoading(true))
            await api.Matriculas.alterarPermissaoCertificado({
              matriculaId,
              permissao: permiteEmissaoCertificado,
            })
            toast.success('Permissão de emissão de certificado alterado!')
            dispatch(ProgressoStore.actions.setLoading(false))
          } catch (error: any) {
            dispatch(ProgressoStore.actions.setLoading(false))
            const message = error?.response?.data.message ?? 'Erro ao reemitir Cobrança!'
            toast.error(message)
          }
        }
      },
      reenviarConclusao(matricula: MatriculaModel, modulo: 'TEORICO' | 'PRATICO') {
        return async (dispatch, getState) => {
          try {
            dispatch(ProgressoStore.actions.setLoading(true))
            await api.Matriculas.detranConcluir({
              matriculaId: matricula.id,
              modulo,
              ignorarFinalizado: true,
            })
            toast.success('Conclusão reenviada com sucesso!')
            getDados(matricula.aluno.id, matricula.id)
          } catch (error: any) {
            const message = error?.response?.data.message ?? 'Erro ao reenviar conclusão!'
            toast.error(message)
          } finally {
            dispatch(ProgressoStore.actions.setLoading(false))
          }
        }
      },
    },
  }
)

const getDados = async (alunoId: number, matriculaId: number) => {
  const [matricula, progressos, outrosCursos] = await Promise.all([
    api.Matriculas.get(matriculaId),
    api.Progresso.getByAluno(alunoId, { matriculaId }),
    api.Usuarios.getCursos(alunoId),
  ])

  return { matricula, progressos, outrosCursos }
}
